/**
 * Frontend Ambition API
 *
 * @usage `import "@api"` and that's it! Access the Ambition global JS variable.
 *
 */
import Vue from "vue"
import { ApiModule } from "@api/src/ApiModule"
import { UI } from "@api/modules/UI"
import { Logger } from "@api/modules/Logger"
import { User } from "@api/modules/User"
import { Request } from "@api/modules/Request"
import { Statuses } from "@api/modules/Statuses"
import { Helpers } from "@api/modules/Helpers"
import { Router } from "@api/modules/Router"
import { Validate } from "@api/modules/Validate"
import { Application } from "@api/modules/Application"
import { Mask } from "@api/modules/Mask"
import _ from "lodash"
import Mixins from "@api/mixins"

// The main API class
class Api extends ApiModule {
    constructor() {
        super()

        // API module singleton classes
        this.Request = new Request()
        this.UI = new UI()
        this.User = new User()
        this.Helpers = new Helpers()
        this.Router = new Router()
        this.Logger = new Logger()
        this.Application = new Application()
        this.Statuses = new Statuses()
        this.Mask = new Mask()
        // API instantiable classes
        this.Validate = Validate
    }

    VueMixin(name) {
        return Mixins[name] || false
    }
}

// Access the Api globally
const Ambition = (window.Ambition = window.Api = new Api())

const isDev = Ambition.isDev()

if (isDev) {
    Vue.config.devtools = true
    Vue.config.performance = true
}

// Event Bus
let eventBus = {
    install(Vue, options) {
        Vue.prototype.$bus = new Vue()
    }
}
Vue.use(eventBus)

let dataDogLocalUser = process.env.MIX_DATADOG_LOCAL_USER

// Datadog Error Logging
// prevent any issues in case of a loading failure of the library
if (window.DD_LOGS && !isDev || window.DD_LOGS && isDev && dataDogLocalUser)
    DD_LOGS.onReady(function () {
        // set your client token
        DD_LOGS.init({
            clientToken: "pubd66f54c982f9d9ffa4a8461bbc76306c",
            forwardErrorsToLogs: true,
            beforeSend:(log) => {
                if (log.http && log.http.status_code === 0) return false
                if (String(log.message).includes('canceled')) return false
                if (String(log.message).includes('_router')) return false
            }
        })

        if (isDev && dataDogLocalUser) {
            DD_LOGS.addLoggerGlobalContext("localDev", dataDogLocalUser)
        }

        DD_LOGS.addLoggerGlobalContext("service", "browser")
        DD_LOGS.addLoggerGlobalContext("tenant", window.location.host)
        DD_LOGS.addLoggerGlobalContext("version", Ambition.getGlobal("tenant.commit"))
        var user = Ambition.getGlobal("tenant.user")
        if (user) DD_LOGS.addLoggerGlobalContext("user", user)

        var applicationId = Ambition.getGlobal("tenant.applicationId")
        if (applicationId) {
            DD_LOGS.addLoggerGlobalContext("application_id", applicationId)
        }
    })

const APP_VERSION = Ambition.getGlobal("tenant.product")
